/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { AnyAction, createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';

import { ApiStatus } from '../../../../ts';
import { PublishSlice } from '../../../../ts/state';
import { SET_ASSISTANT_ID } from '../session/actionTypes';
import {
  getPublishStatus,
  publishVersion,
  revertVersion,
  switchVersion,
  syncChanges
} from './actions';

const initialState = {
  publishStatus: ApiStatus.IDLE,
  publishError: null,
  revertStatus: ApiStatus.IDLE,
  revertError: null,
  switchStatus: ApiStatus.IDLE,
  switchError: null,
  syncChangesStatus: ApiStatus.IDLE,
  syncChangesError: null,
  syncChangesFetched: null,
  versionBeingReverted: null
};

/**
 * Gets the initial state of the redux slice and apply updates.
 * @param {*} updates The updates to apply to the initial state
 * @returns {*} The slice initial state
 */
export const computeState = (updates?: any): PublishSlice => ({
  ..._.cloneDeep(initialState),
  ...updates
});

// Slice + Reducer
const slice = createSlice({
  name: 'publish',
  initialState: computeState(),
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(publishVersion.pending, (state, _action) => {
        state.publishStatus = ApiStatus.LOADING;
        state.publishError = null;
      })
      .addCase(publishVersion.fulfilled, (state, _action) => {
        state.publishStatus = ApiStatus.SUCCEEDED;
        state.publishError = null;
      })
      .addCase(publishVersion.rejected, (state, action) => {
        state.publishStatus = ApiStatus.ERROR;
        state.publishError = action.error;
      })
      .addCase(getPublishStatus.fulfilled, (state, action) => {
        state.publishStatus = action.payload.is_publishing
          ? ApiStatus.LOADING
          : ApiStatus.SUCCEEDED;
        state.publishError = null;
      })
      .addCase(revertVersion.pending, (state, action) => {
        state.versionBeingReverted = action.meta?.arg?.targetVersion;
        state.revertStatus = ApiStatus.LOADING;
        state.revertError = null;
      })
      .addCase(revertVersion.fulfilled, (state, _action) => {
        state.versionBeingReverted = null;
        state.revertStatus = ApiStatus.SUCCEEDED;
        state.revertError = null;
      })
      .addCase(revertVersion.rejected, (state, action) => {
        state.versionBeingReverted = null;
        state.revertStatus = ApiStatus.ERROR;
        state.revertError = action.error;
      })
      .addCase(switchVersion.pending, (state, _action) => {
        state.switchStatus = ApiStatus.LOADING;
        state.switchError = null;
      })
      .addCase(switchVersion.fulfilled, (state, _action) => {
        state.switchStatus = ApiStatus.SUCCEEDED;
        state.switchError = null;
      })
      .addCase(switchVersion.rejected, (state, action) => {
        state.switchStatus = ApiStatus.ERROR;
        state.switchError = action.error;
      })
      .addCase(syncChanges.pending, (state, _action) => {
        state.syncChangesStatus = ApiStatus.LOADING;
        state.syncChangesError = null;
      })
      .addCase(syncChanges.fulfilled, (state, _action) => {
        state.syncChangesStatus = ApiStatus.SUCCEEDED;
        state.syncChangesFetched = Date.now();
        state.syncChangesError = null;
      })
      .addCase(syncChanges.rejected, (state, action) => {
        state.syncChangesStatus = ApiStatus.ERROR;
        state.syncChangesError = action.error;
      })
      .addCase<string, AnyAction>(SET_ASSISTANT_ID, (state, action) => {
        if (action.assistantId) {
          return computeState();
        }
      });
  }
});

export default slice.reducer;
